import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Privacy Policy - Logardt Group</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#FAFAFA', padding: '40px 7.6923%' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-transparent border px-6 py-3 cursor-pointer mb-10 text-base transition-colors hover:bg-opacity-10"
            style={{ 
              borderColor: '#E5A93D',
              color: '#0D2C22',
              borderRadius: '0px'
            }}
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>

          <h1 className="mb-10" style={{ color: '#0D2C22', fontSize: '48px', fontFamily: 'Georgia, serif' }}>
            Privacy Policy
          </h1>

          <div style={{ color: '#0D2C22', fontSize: '16px', lineHeight: '1.8' }}>
            <p className="mb-5" style={{ color: '#666666' }}>
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              1. Introduction
            </h2>
            <p className="mb-5">
              Logardt Group KB ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              2. Information We Collect
            </h2>
            <p className="mb-3" style={{ fontWeight: '600' }}>Personal Information</p>
            <p className="mb-5">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
              <li>Fill out our contact form</li>
              <li>Request our services</li>
              <li>Subscribe to our communications</li>
              <li>Communicate with us via email or other channels</li>
            </ul>
            <p className="mb-5">
              This information may include: name, email address, phone number, company name, and any other information you choose to provide.
            </p>

            <p className="mb-3" style={{ fontWeight: '600' }}>Automatically Collected Information</p>
            <p className="mb-5">
              When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              3. How We Use Your Information
            </h2>
            <p className="mb-5">
              We use the information we collect to:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
              <li>Respond to your inquiries and provide customer service</li>
              <li>Deliver services you have requested</li>
              <li>Send you marketing and promotional communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              4. Data Storage and Security
            </h2>
            <p className="mb-5">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage. Contact form submissions are stored securely in our database and transmitted via encrypted email.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              5. Data Sharing and Disclosure
            </h2>
            <p className="mb-5">
              We do not sell, trade, or rent your personal information to third parties. We may share your information with:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
              <li>Service providers who assist us in operating our website and conducting our business</li>
              <li>Professional advisors such as lawyers and accountants</li>
              <li>Authorities when required by law</li>
            </ul>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              6. Your Rights (GDPR)
            </h2>
            <p className="mb-5">
              If you are a resident of the European Economic Area (EEA), you have certain data protection rights:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '20px' }}>
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              7. Data Retention
            </h2>
            <p className="mb-5">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              8. International Data Transfers
            </h2>
            <p className="mb-5">
              Your information may be transferred to and maintained on computers located outside of your country where data protection laws may differ. We ensure appropriate safeguards are in place for such transfers.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              9. Children's Privacy
            </h2>
            <p className="mb-5">
              Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children under 16.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              10. Changes to This Privacy Policy
            </h2>
            <p className="mb-5">
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last updated" date and will be effective as soon as it is accessible.
            </p>

            <h2 style={{ color: '#0D2C22', fontSize: '24px', marginTop: '40px', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>
              Contact Us
            </h2>
            <p style={{ marginBottom: '40px' }}>
              If you have questions or comments about this Privacy Policy or wish to exercise your data protection rights, please contact us through the contact form on our website.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;